import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { ChatbotPrompt, DisplayLocation } from '@repo/types';
import { SECTIONS, DOMAINS, IMPORTANCE } from '@repo/monitoring';
import { API } from 'api';
import { apiBasePath, apiName } from 'assets';

const STALE_TIME = 5 * 60 * 1000;

const fetchChatbotPrompts = async (
  location: DisplayLocation,
): Promise<ChatbotPrompt[]> => {
  const response: { result: ChatbotPrompt[] } = await API.get(
    apiName,
    `${apiBasePath}/v1/chatbot-prompts?location=${location}`,
    {},
    true,
    undefined,
    {
      tags: {
        section: SECTIONS.RESULTS,
        domain: DOMAINS.CONTENT,
        importance: IMPORTANCE.MEDIUM,
      },
      context: {
        displayLocation: location,
      },
    },
  );
  return response?.result ?? [];
};

export const getChatbotPromptsQuery = (location: DisplayLocation | null) => ({
  queryKey: ['chatbotPrompts', location],
  queryFn: (): Promise<ChatbotPrompt[]> =>
    location ? fetchChatbotPrompts(location) : Promise.resolve([]),
  staleTime: STALE_TIME,
});

export const useChatbotPrompts = (location: DisplayLocation | null) => {
  return useQuery({
    ...getChatbotPromptsQuery(location),
    enabled: !!location,
  });
};

// Prompts are global and cached, so warming the cache when the section mounts
// lets the entry-point card render on first paint instead of popping in later.
export const usePrefetchChatbotPrompts = (location: DisplayLocation) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    queryClient.prefetchQuery(getChatbotPromptsQuery(location));
  }, [queryClient, location]);
};
